import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Button,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import TableWrapper from "@/utils/TableWrapper";

/** A translated page of the list, as the table below reads it. */
type TranslatePageRow = {
  _id: string;
  global_title?: string;
  meta_description?: string;
  page_template?: { name?: string };
  parentPage?: { site?: { domain?: string } };
  target_language?: string;
  publish_statement?: boolean;
  requires_review?: boolean;
  createdAt?: string;
};

export type TranslatePagesViewProps = {
  translatePages: TranslatePageRow[];
  loading: boolean;
  onDelete: (page: TranslatePageRow) => void;
  onEdit: (pageId: string) => void;
  onSourceDataClick: (page: TranslatePageRow) => void;
};

/** Status label and badge colour, derived from publish_statement / requires_review. */
const statusOf = (page: TranslatePageRow): { label: string; color: string } => {
  if (page.publish_statement === true && page.requires_review === false) return { label: "Published", color: "success" };
  if (page.publish_statement === false && page.requires_review === false) return { label: "Draft to Publish", color: "warning" };
  if (page.requires_review === true && page.publish_statement === true) return { label: "Error", color: "danger" };
  return { label: "Pending", color: "info" };
};

type Cell = { row: { original: TranslatePageRow } };

// SCRUM-2115: the list renders through TableWrapper (search, paging, sort,
// aria-sort). Its search box replaces the old one, whose value the container
// stored and nothing filtered on. Accessors return the text the cell shows, so
// search and sort read what the editor sees.
const TranslatePagesView = ({
  translatePages,
  loading,
  onDelete,
  onEdit,
  onSourceDataClick,
}: TranslatePagesViewProps) => {
  const columns = [
    {
      id: "title",
      header: "Page Title",
      accessorFn: (page: TranslatePageRow) => page.global_title || "Untitled",
      cell: ({ row: { original: page } }: Cell) => (
        <div className="d-flex align-items-center">
          <div>
            <span className="mb-0 text-sm font-weight-bold">
              {/* Unchanged from the JavaScript: without a title this renders the page_template object (IMPROVEMENTS.md). */}
              {page.global_title || (page.page_template as React.ReactNode) || 'Untitled'}
            </span>
            {page.meta_description && (
              <div className="text-muted small">
                {page.meta_description.substring(0, 50)}
                {page.meta_description.length > 50 ? '...' : ''}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "template",
      header: "Template Type",
      accessorFn: (page: TranslatePageRow) => page.page_template?.name || "Unknown",
      cell: ({ row: { original: page } }: Cell) => (
        <Badge color="secondary" pill>
          {page.page_template?.name || 'Unknown'}
        </Badge>
      ),
    },
    {
      id: "source",
      header: "Source Data",
      accessorFn: (page: TranslatePageRow) => page.parentPage?.site?.domain || "",
      cell: ({ row: { original: page } }: Cell) => (
        <Button
          color="link"
          className="p-0 text-primary text-decoration-underline"
          onClick={() => onSourceDataClick(page)}
          title="Click to view source data details"
        >
          <i className="fas fa-info-circle me-1"></i>
          {page.parentPage?.site?.domain || 'View Details'}
        </Button>
      ),
    },
    {
      id: "language",
      header: "Target Language",
      accessorFn: (page: TranslatePageRow) => page.target_language || "Unknown",
      cell: ({ row: { original: page } }: Cell) => (
        <span className="text-sm">{page.target_language || 'Unknown'}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (page: TranslatePageRow) => statusOf(page).label,
      cell: ({ row: { original: page } }: Cell) => {
        const status = statusOf(page);
        return <Badge color={status.color}>{status.label}</Badge>;
      },
    },
    {
      id: "created",
      header: "Created",
      accessorFn: (page: TranslatePageRow) => page.createdAt || "",
      cell: ({ row: { original: page } }: Cell) => (
        <span className="text-sm">
          {page.createdAt ? new Date(page.createdAt).toLocaleDateString() : 'N/A'}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: ({ row: { original: page } }: Cell) => (
        <div className="d-flex">
          <Button
            color="info"
            size="sm"
            className="mr-2"
            onClick={() => onEdit(page._id)}
            title="Edit Translation"
          >
            <i className="fas fa-edit" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={() => onDelete(page)}
            title="Delete Translation"
          >
            <i className="fas fa-trash" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <SimpleHeader name="Translate Pages" parentName="Pages" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <i className="ni ni-world text-primary mr-2" style={{ fontSize: "1.2rem" }} />
              <h3 className="mb-0 text-primary">Translation Pages</h3>
              <Badge color="info" className="ml-2">
                {translatePages.length} Total
              </Badge>
            </div>
          </CardHeader>

          <CardBody>
            {/* Loading State */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="mt-3">Loading translate pages...</div>
              </div>
            ) : translatePages.length === 0 ? (
              /* Empty State */
              <Alert color="info" className="text-center">
                <i className="ni ni-world mr-2" />
                No translate pages found. Start translating pages to see them here.
              </Alert>
            ) : (
              <TableWrapper
                data={translatePages}
                columns={columns}
                searchLabel="Search translate pages"
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default TranslatePagesView;
